import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchDocuments, DocumentMeta } from "../api/client";
import { AlertTriangle, ChevronDown, ChevronRight, FileText, Eye } from "lucide-react";

/**
 * Parse Result Inspector — admin diagnostic page.
 *
 * Shows raw extraction, parse result, AI fallback status,
 * field-by-field inspection, and missing/defaulted fields
 * for each document in the deal.
 */

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface TenantInspection {
  tenant_name: string;
  parser_lease_type: string;
  parser_cam_type: string;
  parser_pro_rata: number | null;
  engine_lease_type: string;
  engine_cam_type: string;
  engine_pro_rata: string;
  overrides: string[];
  missing_fields: string[];
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function inspectTenant(raw: Record<string, unknown>): TenantInspection {
  const td = (raw.tenant ?? raw) as Record<string, unknown>;
  const cam = (td.cam_structure ?? {}) as Record<string, unknown>;

  const parserLease = (td.lease_type as string) ?? "NOT_SET";
  const parserCam = (cam.cam_type as string) ?? "NOT_SET";
  const parserProRata = td.pro_rata_share as number | null;

  // Derive what the engine will actually use (mirrors deals.py logic)
  const isPending = !parserLease || parserLease === "pending";
  const engineLease = isPending ? "NNN" : parserLease;

  const parserHasCam = parserCam && parserCam !== "gross" && parserCam !== "NOT_SET";
  let engineCam: string;
  if (parserHasCam) {
    engineCam = parserCam;
  } else if (engineLease === "NNN") {
    engineCam = "NNN";
  } else if (engineLease === "gross" || engineLease === "FSG") {
    engineCam = "gross";
  } else {
    engineCam = "NNN";
  }

  const overrides: string[] = [];
  if (isPending) {
    overrides.push(`lease_type: pending \u2192 ${engineLease} (default)`);
  }
  if (!parserHasCam && engineCam !== parserCam) {
    overrides.push(`cam_type: ${parserCam || "none"} \u2192 ${engineCam} (default)`);
  }

  const missing: string[] = [];
  if (!td.base_rent_annual_psf) missing.push("base_rent_annual_psf");
  if (!td.lease_start_date) missing.push("lease_start_date");
  if (!td.lease_expiration_date) missing.push("lease_expiration_date");
  if (!td.leased_sf) missing.push("leased_sf");
  if (!td.tenant_name) missing.push("tenant_name");

  return {
    tenant_name: (td.tenant_name as string) ?? "Unknown",
    parser_lease_type: parserLease,
    parser_cam_type: parserCam,
    parser_pro_rata: parserProRata,
    engine_lease_type: engineLease,
    engine_cam_type: engineCam,
    engine_pro_rata: parserProRata != null ? String(parserProRata) : "auto-calculated",
    overrides,
    missing_fields: missing,
  };
}

/* ------------------------------------------------------------------ */
/* Collapsible JSON viewer                                             */
/* ------------------------------------------------------------------ */

function JsonBlock({ label, data }: { label: string; data: unknown }) {
  const [open, setOpen] = useState(false);
  if (data == null) return null;

  return (
    <div className="border border-bark-200 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-2 px-4 py-2 bg-grove-50 text-left font-medium text-sm text-forest-700 hover:bg-grove-100"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {label}
      </button>
      {open && (
        <pre className="p-4 text-xs overflow-auto max-h-96 bg-white font-mono">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Document Inspector Card                                             */
/* ------------------------------------------------------------------ */

function DocInspector({ doc }: { doc: DocumentMeta }) {
  const pr = doc.parse_result as Record<string, unknown> | null;
  const tenants = (pr?.tenants ?? []) as Record<string, unknown>[];
  const inspected = tenants.map(inspectTenant);
  const overrideCount = inspected.filter((t) => t.overrides.length > 0).length;
  const missingCount = inspected.filter((t) => t.missing_fields.length > 0).length;

  const aiFallback = pr?.ai_fallback as boolean | undefined;
  const aiFallbackReason = pr?.ai_fallback_reason as string | undefined;
  const method = pr?.extraction_method as string | undefined;
  const confidence = pr?.confidence_score as number | undefined;

  return (
    <div className="border border-bark-200 rounded-xl bg-white p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-forest-800 flex items-center gap-2">
            <FileText size={16} />
            {doc.filename}
          </h3>
          <p className="text-xs text-bark-500 mt-0.5">
            {doc.file_type} &middot; {doc.parse_status}
            {method && <> &middot; extraction: <span className="font-mono">{method}</span></>}
            {confidence != null && <> &middot; confidence: {confidence}</>}
          </p>
        </div>
        {aiFallback && (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
            <AlertTriangle size={12} />
            AI Fallback
          </span>
        )}
      </div>

      {/* AI Fallback Warning */}
      {aiFallback && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <strong>AI extraction was not used.</strong> Reason: {aiFallbackReason ?? "unknown"}.
          Lease types and CAM structures were determined by rule-based parsing
          and may be incorrect.
        </div>
      )}

      {/* Parse Error */}
      {doc.parse_error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900">
          <strong>Parse Error:</strong> {doc.parse_error}
        </div>
      )}

      {/* Override Summary */}
      {overrideCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <strong>{overrideCount} of {inspected.length} tenants</strong> have
          fields where the parser returned &quot;pending&quot; or &quot;unknown&quot; values
          that were replaced with defaults.
        </div>
      )}
      {missingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <strong>{missingCount} tenants</strong> have missing required fields.
        </div>
      )}

      {/* Tenant-by-tenant inspection */}
      {inspected.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-grove-50 text-left">
                <th className="px-3 py-2 font-medium text-forest-700">Tenant</th>
                <th className="px-3 py-2 font-medium text-forest-700">Parser lease_type</th>
                <th className="px-3 py-2 font-medium text-forest-700">Parser cam_type</th>
                <th className="px-3 py-2 font-medium text-forest-700">Engine lease_type</th>
                <th className="px-3 py-2 font-medium text-forest-700">Engine cam_type</th>
                <th className="px-3 py-2 font-medium text-forest-700">Overrides</th>
                <th className="px-3 py-2 font-medium text-forest-700">Missing</th>
              </tr>
            </thead>
            <tbody>
              {inspected.map((t, i) => (
                <tr key={i} className={`border-t border-bark-100 ${t.overrides.length > 0 ? "bg-red-50" : ""}`}>
                  <td className="px-3 py-2 font-medium">{t.tenant_name}</td>
                  <td className="px-3 py-2 font-mono">{t.parser_lease_type}</td>
                  <td className="px-3 py-2 font-mono">{t.parser_cam_type}</td>
                  <td className="px-3 py-2 font-mono text-red-700">{t.engine_lease_type}</td>
                  <td className="px-3 py-2 font-mono text-red-700">{t.engine_cam_type}</td>
                  <td className="px-3 py-2">
                    {t.overrides.map((o, j) => (
                      <div key={j} className="text-red-700">{o}</div>
                    ))}
                  </td>
                  <td className="px-3 py-2">
                    {t.missing_fields.map((f, j) => (
                      <div key={j} className="text-amber-700">{f}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Raw data sections */}
      <div className="space-y-2">
        <JsonBlock label="Raw Extraction" data={doc.raw_extraction} />
        <JsonBlock label="Parse Result" data={doc.parse_result} />
        <JsonBlock label="Audit Result" data={doc.audit_result} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function ParseInspectorPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const [docs, setDocs] = useState<DocumentMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      const result = await fetchDocuments(dealId);
      setDocs(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          Failed to load documents: {error}
        </div>
      </div>
    );
  }

  const parsedDocs = docs.filter((d) => d.parse_status === "complete");
  const failedDocs = docs.filter((d) => d.parse_status === "error");

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Eye size={24} className="text-forest-600" />
        <h1 className="text-2xl font-bold font-['Lora'] text-forest-800">
          Parse Result Inspector
        </h1>
      </div>

      <p className="text-sm text-bark-600">
        Diagnostic view showing raw extraction data, parse results, AI fallback
        status, and field-by-field comparison between parser output and engine
        input for each document in this deal.
      </p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-bark-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-forest-700 tabular-nums">{docs.length}</div>
          <div className="text-xs text-bark-500">Total Documents</div>
        </div>
        <div className="bg-white border border-bark-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-700 tabular-nums">{parsedDocs.length}</div>
          <div className="text-xs text-bark-500">Parsed OK</div>
        </div>
        <div className="bg-white border border-bark-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-700 tabular-nums">{failedDocs.length}</div>
          <div className="text-xs text-bark-500">Parse Errors</div>
        </div>
        <div className="bg-white border border-bark-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-amber-700 tabular-nums">
            {parsedDocs.filter((d) => (d.parse_result as Record<string, unknown>)?.ai_fallback).length}
          </div>
          <div className="text-xs text-bark-500">AI Fallbacks</div>
        </div>
      </div>

      {/* Failed docs */}
      {failedDocs.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-red-800">Failed Parses</h2>
          {failedDocs.map((doc) => (
            <DocInspector key={doc.id} doc={doc} />
          ))}
        </div>
      )}

      {/* Parsed docs */}
      {parsedDocs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-forest-800">Parsed Documents</h2>
          {parsedDocs.map((doc) => (
            <DocInspector key={doc.id} doc={doc} />
          ))}
        </div>
      )}

      {docs.length === 0 && (
        <div className="text-center py-12 text-bark-500">
          No documents uploaded to this deal yet.
        </div>
      )}
    </div>
  );
}
